import { clusterAppearance } from './cluster-style'
import { mapMotionDefaults } from './motion-options'

// 本地大屏地图的整套默认外观；只保存声明式样式，不保存截图或设备位置。
export const mapPresentationDefaults = Object.freeze({
  ...mapMotionDefaults,
  mapStyleVersion: 1, areaColor: '#0b3762', empAreaColor: '#185582',
  borderColor: '#51bde5', borderWidth: .7, pointColor: '#40dcff',
  pointSymbol: 'diamond', pointSize: 10, clusterColor: '#27d9ff',
  clusterSize: 1, glowStrength: 45,
  showDeviceLabel: false, showRegionLabel: false, showInfo: true
})

export function mapPresentation (input = {}) {
  const source = { ...input }
  // 只升级上一轮原封不动的预置配色，不覆盖用户自定义主题。
  if (!source.mapStyleVersion && source.areaColor === '#061d33' && source.borderColor === '#0dffff' &&
      source.empAreaColor === 'rgba(35, 183, 229, 0.42)') {
    for (const key of ['areaColor', 'empAreaColor', 'borderColor']) delete source[key]
    for (const [key, value] of Object.entries({ borderWidth: 1, pointColor: '#38bdf8', pointSymbol: 'circle', pointSize: 8 })) if (source[key] === value) delete source[key]
  }
  return { ...mapPresentationDefaults, ...source }
}

const limit = (value, fallback, min, max) => Number.isFinite(Number(value)) ? Math.max(min, Math.min(max, Number(value))) : fallback
const named = { red: '#ff0000', blue: '#0000ff', cyan: '#00ffff', white: '#ffffff', black: '#000000', orange: '#ffa500', yellow: '#ffff00', green: '#008000' }
function channels (input) {
  const color = String(named[input] || input || '').trim()
  const hex = /^#([\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/i.exec(color)
  if (hex) {
    const value = hex[1].length === 3 ? hex[1].split('').map(char => char + char).join('') : hex[1]
    return [...[0, 2, 4].map(index => parseInt(value.slice(index, index + 2), 16)), value.length === 8 ? parseInt(value.slice(6), 16) / 255 : 1]
  }
  const rgb = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i.exec(color)
  return rgb ? [...rgb.slice(1, 4).map(value => Math.min(255, Math.round(Number(value)))), rgb[4] === undefined ? 1 : Math.min(1, Number(rgb[4]))] : null
}

export function mapTint (color, factor = 0, opacity = 1) {
  const rgb = channels(color) || channels(mapPresentationDefaults.pointColor)
  const target = factor < 0 ? 0 : 255, amount = Math.min(1, Math.abs(factor))
  return 'rgba(' + rgb.slice(0, 3).map(value => Math.round(value + (target - value) * amount)).join(',') + ',' + Math.max(0, Math.min(1, opacity * rgb[3])) + ')'
}

export function mapSurface (color) {
  if (!channels(color)) return color
  return { type: 'linear', x: 0, y: 0, x2: 1, y2: 1, colorStops: [
    { offset: 0, color: mapTint(color, .13) }, { offset: .45, color: mapTint(color) }, { offset: 1, color: mapTint(color, -.5) }
  ] }
}

const badgeCache = new Map()
// SVG仅含固定几何和经过颜色解析的数值；设备名称不会被插入SVG。
export function mapClusterBadge (count, option) {
  const look = clusterAppearance(count), scale = limit(option.clusterSize, 1, .7, 1.6)
  const width = Math.max(64, 36 + look.label.length * 9), height = 62
  const accent = mapTint(option.clusterColor || option.pointColor), soft = mapTint(option.clusterColor || option.pointColor, 0, .18)
  const key = [width, accent, soft].join('|')
  if (!badgeCache.has(key)) {
    const x = width / 2
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><defs><linearGradient id="body" x2="0" y2="1"><stop stop-color="#15384f" stop-opacity=".96"/><stop offset="1" stop-color="#051a2e" stop-opacity=".97"/></linearGradient></defs><ellipse cx="${x}" cy="56" rx="15" ry="4" fill="${soft}"/><path d="M${x} 34V53" stroke="${accent}" stroke-width="1"/><path d="M10 1H${width - 1}V25L${width - 10} 35H1V10Z" fill="url(#body)" stroke="${soft}"/><path d="M2 13V10L11 1H24M${width - 2} 23V25L${width - 11} 34H${width - 28}" fill="none" stroke="${accent}" stroke-width="2"/><path d="M12 2H${width - 4}" stroke="#b9efff" stroke-opacity=".22"/><path d="M${x} 51L${x + 4} 55L${x} 59L${x - 4} 55Z" fill="${accent}"/></svg>`
    badgeCache.set(key, 'image://data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg))
    if (badgeCache.size > 64) badgeCache.delete(badgeCache.keys().next().value)
  }
  return { symbol: badgeCache.get(key), width: width * scale, height: height * scale,
    offset: (height / 2 - 55) * scale, labelOffset: (18 - height / 2) * scale,
    fontSize: 18 * scale, unitSize: 11 * scale, label: look.label, accent }
}

export function shortMapName (input, fontSize = 12) {
  const full = String(input == null ? '' : input), max = 120, size = Math.max(10, Number(fontSize) || 12)
  let width = 0, result = ''
  for (const char of Array.from(full)) {
    width += (char.codePointAt(0) > 255 ? 1 : .6) * size
    if (width > max - size) return result + '…'
    result += char
  }
  return result
}

const escape = value => String(value == null ? '' : value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]))
export function mapTooltip (row, canZoom = true, option = {}) {
  if (!row) return ''
  if (row.regionCount) {
    const unit = option.regionShowUnit === false ? '' : option.regionUnit === undefined ? '台' : String(option.regionUnit).slice(0, 24)
    return '<div class="device-map-tooltip"><strong>' + escape(row.name) + '</strong><div style="margin-top:5px">' + escape(row.count) + (unit ? ' ' + escape(unit) + (unit === '台' ? '设备' : '') : '') + '</div></div>'
  }
  const count = row.clusterCount || row.clusterRows && row.clusterRows.length
  if (count > 1) return '<div class="device-map-tooltip"><strong>该区域 ' + count + ' 台设备</strong>' + (canZoom ? '<div style="color:#9cbacb;font-size:12px;margin-top:5px">点击放大查看</div>' : '') + '</div>'
  return '<div class="device-map-tooltip" role="tooltip" style="max-width:260px;white-space:normal;overflow-wrap:anywhere;word-break:break-word">' +
    '<div style="font-size:12px;color:#9cbacb;margin-bottom:6px">设备名称</div>' +
    '<div style="font-size:14px;line-height:1.65;font-weight:500">' + escape(row.name || row.deviceId || '未命名设备') + '</div></div>'
}

export function mapTooltipPosition (point, params, dom, rect, size) {
  const [width, height] = size.viewSize, [contentWidth, contentHeight] = size.contentSize
  if (dom && dom.style) {
    dom.style.maxWidth = Math.max(1, Math.min(284, width - 16)) + 'px'
    dom.style.maxHeight = Math.max(1, height - 16) + 'px'
    dom.style.overflowY = 'auto'
  }
  return [Math.max(8, Math.min(point[0] + 16, width - contentWidth - 8)), Math.max(8, Math.min(point[1] + 16, height - contentHeight - 8))]
}
